import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';

import { catchError, map, of, startWith, switchMap } from 'rxjs';

import { BcmBookingsApiService } from '@bcmApiServices/bcm-bookings.api-service';
import { UserService } from '@core/services/user.service';
import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { BcmFinancialRecord } from '@shared/models/bcm-financial-record';
import { TolgeeService } from '@core/translation/tolgee.service';
import { TranslationModule } from '@core/translation/translation.module';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { BcmService } from '@modules/bcm/bcm.service';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import {
    getFinancialRecordTypeLabel,
    hasFinancialRecordQuote,
    resolveFinancialRecordNavigationPath,
} from '@modules/bcm/@shared/components/invoices/financial-record-document.util';
import { U2bSegmentedToggleComponent, U2bSegmentedToggleOption } from '@u2bUi/segmented-toggle/u2b-segmented-toggle.component';

import { BookingV2DynamicPriceBreakdownComponent } from '../../components/dynamic-price-breakdown/booking-v2-dynamic-price-breakdown.component';
import { RESERVATION_DEPOSIT_TITLE_KEY } from '../../dtos/booking-v2.dtos';
import { BookingV2BerthProductService } from '../../services/booking-v2-berth-product.service';
import { isDroppedBerthLine } from '../../services/booking-v2-payload.mapper';
import { BookingV2BelegSource, BookingV2Facade, BookingV2Step } from '../../services/booking-v2.facade';
import { keyHasPersistedBorrower } from '../../utils/booking-v2-deposit-billing';
import { isElectricBelegEligible } from '../../utils/booking-v2-electric-billing';
import {
    relationProductIsUnbillable,
    relationProductQuantity,
    relationProductTitle,
    relationProductUnitPrice,
} from '../../utils/booking-v2-relation-billing';
import { bookingV2RechnungsversandRelationLabelKey } from '../../utils/booking-v2-relation-label.util';
import { formatMoney, formatQuantity, grossOf } from '../../utils/booking-v2-pricing';

type BelegKind = 'invoice' | 'receipt';

// One billable line shown in the dispatch step, aggregated from every cost source
// the booking actually creates: the Beziehung's Leistungen, the advanced
// one-off services, subscriptions (Abo, first period only), flat-rate disconnected
// electric connections, and key deposits (Pfand).
interface RechnungsversandRow {
    key: string;
    source: BookingV2BelegSource;
    // Used after save to match this row to the created position (catalog → by
    // productId; ad-hoc → by name; electric matches electric positions only).
    productId: number | null;
    name: string;
    quantity: number;
    unitLabelKey: string | null;
    gross: number;
    // Rule-priced line: carries the evaluated breakdown so the dispatch overview
    // can show the same price composition as the relation card / services tab.
    hasDynamicPrice: boolean;
    dynamicPrice: Partial<BcmDynamicPriceDto> | null;
    // False for a berth product that produces no position (quantity 0, or a zero
    // total the berth product is configured to skip): listed so the user sees it,
    // but not tickable — arming the Beleg with it would transfer nothing.
    billable: boolean;
}

@Component({
    selector: 'booking-v2-rechnungsversand-step',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        Button,
        ButtonIcon,
        ButtonLabel,
        Checkbox,
        TranslationModule,
        U2bSegmentedToggleComponent,
        BookingV2DynamicPriceBreakdownComponent,
    ],
    templateUrl: './rechnungsversand-step.component.html',
    host: { class: 'block' },
})
export class RechnungsversandStepComponent {

    private readonly facade = inject(BookingV2Facade);
    private readonly berthProducts = inject(BookingV2BerthProductService);
    private readonly bookingsApi = inject(BcmBookingsApiService);
    private readonly bcmNavigation = inject(BcmNavigationService);
    private readonly userService = inject(UserService);

    // A berth whose Produkte could not be loaded contributes no lines — without this
    // it looks exactly like a berth that has none, and the booking saves silently
    // without them.
    readonly berthProductsLoadFailed = this.berthProducts.loadFailed;
    private readonly bcm = inject(BcmService);

    readonly showSavedBookingDocuments = computed(() => this.facade.bookingId() != null);

    private readonly savedDocumentsState = toSignal(
        toObservable(this.facade.bookingId).pipe(
            switchMap(bookingId => {
                if (bookingId == null) {
                    return of({ loading: false, failed: false, records: [] as BcmFinancialRecord[] });
                }
                return this.bookingsApi.getFinancialRecords(bookingId).pipe(
                    map(records => ({ loading: false, failed: false, records })),
                    // A failed load must not read as "no Belege yet" (duplicate-invoice risk).
                    catchError(() => of({ loading: false, failed: true, records: [] as BcmFinancialRecord[] })),
                    startWith({ loading: true, failed: false, records: [] as BcmFinancialRecord[] }),
                );
            }),
        ),
        { initialValue: { loading: false, failed: false, records: [] as BcmFinancialRecord[] } },
    );

    readonly savedDocumentsLoading = computed(() => this.savedDocumentsState().loading);
    readonly savedDocumentsLoadFailed = computed(() => this.savedDocumentsState().failed);
    readonly savedDocuments = computed(() => this.savedDocumentsState().records);

    financialRecordTypeLabel(record: BcmFinancialRecord): string {
        return getFinancialRecordTypeLabel(record.type);
    }

    // Mirrors the target routes' guards, so the wizard is not closed onto a
    // permission-denied dialog.
    canOpenSavedDocument(record: BcmFinancialRecord): boolean {
        const path = resolveFinancialRecordNavigationPath(record);
        if (!path) {
            return false;
        }
        if (!hasFinancialRecordQuote(record)) {
            return this.userService.hasAllPermissionsOf(BcmUserPermission.INVOICES_READ);
        }
        const isDraftQuote = path[2] !== 'view';
        return isDraftQuote
            ? this.userService.hasAllPermissionsOf(BcmUserPermission.QUOTES_READ, BcmUserPermission.QUOTES_WRITE)
            : this.userService.hasAllPermissionsOf(BcmUserPermission.QUOTES_READ);
    }

    // Goes through the dialog's unsaved-changes guard; nothing was saved here, so
    // the dialog closes without a result (callers must not reload / toast).
    openSavedDocument(record: BcmFinancialRecord): void {
        const path = resolveFinancialRecordNavigationPath(record);
        if (!path) {
            return;
        }
        this.facade.requestCloseThen(() => void this.bcmNavigation.navigate(path));
    }

    fmt = formatMoney;

    fmtQty = formatQuantity;

    rechnungsversandRelationKey(): string {
        const tenant = this.bcm.tenant;
        return bookingV2RechnungsversandRelationLabelKey(tenant.registerType, tenant.productType);
    }

    // Required entries still missing before the booking can be saved (Save lives
    // on this last step, so the blocker is surfaced here with jump-to-step links).
    goToStep(step: BookingV2Step): void {
        // The advanced step only exists in advanced mode; enable it so the jump
        // (e.g. an incomplete subscription) doesn't get bounced back by the facade.
        if (step === 'advanced' && !this.facade.advancedMode()) {
            this.facade.advancedMode.set(true);
        }
        this.facade.goToStep(step);
    }

    readonly kindOptions: U2bSegmentedToggleOption<BelegKind>[] = [
        { value: 'invoice', icon: 'receipt_long', labelKey: 'bookingV2.rechnungsversand.kind.invoice' },
        { value: 'receipt', icon: 'point_of_sale', labelKey: 'bookingV2.rechnungsversand.kind.receipt' },
    ];

    // Liegerkategorie Leistungen (pre-resolved on the facade). The quantity comes from
    // the very helper the billed position uses (relationProductQuantity) so the shown
    // gross matches the invoice; an open-ended relation bills one period via the scheduler.
    private readonly relationRows = computed<RechnungsversandRow[]>(() => {
        const relation = this.facade.selectedRelation();
        const products = relation?.products ?? [];
        if (!products.length) {
            return [];
        }
        const { from, to } = this.facade.dateRange();
        const payableOption = relation?.payableOption ?? null;
        const timeUnit = this.facade.defaultTimeUnit();
        const overrides = this.facade.effectiveRelationLineOverrides();
        // Ein Produkt, dessen Preisregel nicht greift, wird nicht berechnet und darf
        // hier auch nicht auftauchen — die Vorschau muss zeigen, was der Beleg trägt. (#7017)
        return products.filter(p => !relationProductIsUnbillable(p)).map(p => {
            const quantity = relationProductQuantity(p, from, to, payableOption, timeUnit);
            return {
                key: `relation:${p.productId}`,
                source: 'relation' as const,
                productId: p.productId,
                name: relationProductTitle(p, overrides[p.productId]),
                quantity,
                unitLabelKey: p.unitLabelKey,
                gross: grossOf(quantity, relationProductUnitPrice(p, overrides[p.productId]), p.vatValue),
                hasDynamicPrice: p.hasDynamicPrice,
                dynamicPrice: p.dynamicPrice ?? null,
                billable: true,
            };
        });
    });

    // One-off advanced services (settled/protected lines are already billed).
    // A line seeded from the berth's Produkte (#6734) is shown as its own source so
    // the user can tell where it came from; it is listed even at quantity 0 (the
    // berth product is a placeholder to be filled in), but then not billable.
    private readonly serviceRows = computed<RechnungsversandRow[]>(() =>
        this.facade.services()
            .filter(s => !s.isSubscription && !s.isProtected)
            .map(s => ({
                key: `service:${s.id}`,
                source: (s.berthOrigin?.managed ? 'berth' : 'service') as BookingV2BelegSource,
                productId: s.productId,
                name: s.name,
                quantity: s.quantity,
                unitLabelKey: s.unitLabelKey,
                gross: grossOf(s.quantity, s.price, s.vatValue, s.discount),
                hasDynamicPrice: !!s.hasDynamicPrice,
                dynamicPrice: s.dynamicPrice ?? null,
                // Exactly the rule the commit applies, so what is tickable here and
                // what ends up on the Beleg can never drift apart.
                billable: !isDroppedBerthLine(s),
            })),
    );

    // Subscriptions (Abo): only the first period is billed now — the scheduler
    // stamps that position with this booking on save; later periods follow.
    // Incomplete rows (no product/interval) are dropped by the commit, so skipped.
    private readonly subscriptionRows = computed<RechnungsversandRow[]>(() =>
        this.facade.services()
            .filter(s => s.isSubscription && !s.isProtected
                && s.productId != null && s.subscriptionConfig?.payableOptionId != null)
            .map(s => ({
                key: `subscription:${s.id}`,
                source: 'subscription' as const,
                productId: s.productId,
                name: s.name,
                quantity: s.quantity,
                unitLabelKey: s.unitLabelKey,
                gross: grossOf(s.quantity, s.price, s.vatValue, s.discount),
                hasDynamicPrice: !!s.hasDynamicPrice,
                dynamicPrice: s.dynamicPrice ?? null,
                billable: true,
            })),
    );

    // Strom: connections whose amount is a known fixed sum (product × quantity).
    // Prepaid is billed upfront → shown as soon as it has a product; flat-rate
    // (Festbetrag) only once disconnected (getrennt → end date set). Consumption
    // (BY_USAGE) and NO_CHARGE are settled via the power tab, not here.
    private readonly electricRows = computed<RechnungsversandRow[]>(() =>
        this.facade.electricMeterAssignments()
            .filter(isElectricBelegEligible)
            .map(m => {
                const quantity = m.quantity ?? 1;
                return {
                    key: `electric:${m.uuid}`,
                    source: 'electric' as const,
                    productId: m.productId,
                    name: m.productName ?? m.meterFullName,
                    quantity,
                    unitLabelKey: null,
                    gross: grossOf(quantity, m.productPrice as number, m.productVatValue ?? 0),
                    hasDynamicPrice: false,
                    dynamicPrice: null,
                    billable: true,
                };
            }),
    );

    // Key deposits (Pfand): one quantity-1 line per lent key with a deposit product.
    // `keyHasPersistedBorrower` keeps this in lockstep with mapKeyDepositPositions —
    // a company-only key produces no deposit position, so it must not inflate the
    // summary total either.
    private readonly depositRows = computed<RechnungsversandRow[]>(() =>
        this.facade.keys()
            .filter(k => keyHasPersistedBorrower(k)
                && k.depositProductId != null && k.depositAmount != null && k.depositAmount > 0)
            .map(k => ({
                key: `deposit:${k.uuid}`,
                source: 'deposit' as const,
                productId: k.depositProductId,
                name: k.depositProductName ?? k.keyName,
                quantity: 1,
                unitLabelKey: null,
                gross: grossOf(1, k.depositAmount as number, k.depositVatValue ?? 0),
                hasDynamicPrice: false,
                dynamicPrice: null,
                billable: true,
            })),
    );

    // Reservation down payment (create-only) — a synthetic ad-hoc row that
    // matches the deposit position saved by the commit (by title, productId null).
    private readonly reservationDepositRows = computed<RechnungsversandRow[]>(() =>
        this.showDepositRow()
            ? [{
                key: 'deposit',
                source: 'deposit' as const,
                productId: null,
                name: TolgeeService.translate(RESERVATION_DEPOSIT_TITLE_KEY),
                quantity: 1,
                unitLabelKey: null,
                gross: grossOf(1, this.facade.depositAmount() ?? 0, this.facade.depositVatValue()),
                hasDynamicPrice: false,
                dynamicPrice: null,
                billable: true,
            }]
            : [],
    );

    private readonly showDepositRow = computed(() =>
        this.facade.bookingType() === 'reservation'
        && this.facade.depositRequired()
        && !this.facade.isEditMode()
        && (this.facade.depositAmount() ?? 0) > 0,
    );

    readonly rows = computed<readonly RechnungsversandRow[]>(() => [
        ...this.relationRows(),
        ...this.serviceRows(),
        ...this.subscriptionRows(),
        ...this.electricRows(),
        ...this.depositRows(),
        ...this.reservationDepositRows(),
    ]);

    // Relation + subscription rows show catalog/first-period estimates (the real
    // billing runs through the subscription engine), so the price hint is shown
    // whenever such a row is present.
    readonly hasRelationRows = computed(() =>
        this.rows().some(r => r.source === 'relation' || r.source === 'subscription'),
    );

    private readonly billableRows = computed(() => this.rows().filter(r => r.billable));

    // A berth product left at quantity 0 is listed (so the user sees it and can fill
    // it in) but creates no position — hence the hint pointing at the Leistungen tab.
    readonly hasUnbillableBerthRow = computed(() =>
        this.rows().some(r => r.source === 'berth' && !r.billable),
    );

    // Selection lives on the facade so it survives step re-mounts and the dialog
    // can match exactly the ticked rows to the saved positions.
    private readonly selectedKeys = computed(() => new Set(this.facade.belegSelection().map(i => i.key)));

    readonly selectedRows = computed(() => this.billableRows().filter(r => this.selectedKeys().has(r.key)));
    readonly selectedCount = computed(() => this.selectedRows().length);
    readonly selectedGross = computed(() => this.selectedRows().reduce((sum, r) => sum + r.gross, 0));

    readonly allSelected = computed(() =>
        this.billableRows().length > 0 && this.billableRows().every(r => this.selectedKeys().has(r.key)),
    );
    readonly someSelected = computed(() =>
        this.billableRows().some(r => this.selectedKeys().has(r.key)) && !this.allSelected(),
    );

    readonly belegKind = computed<BelegKind>(() => this.facade.belegIntent().kind);
    readonly armed = computed(() => this.facade.belegIntent().create);

    constructor() {
        // Drop ticks whose row vanished or stopped being billable (e.g. a berth
        // product set back to quantity 0). selectBelegPositions matches by product
        // id, so a stale item would otherwise claim an unrelated position.
        effect(() => {
            const billable = new Set(this.billableRows().map(r => r.key));
            const selection = this.facade.belegSelection();
            if (selection.every(i => billable.has(i.key))) {
                return;
            }
            this.facade.belegSelection.set(selection.filter(i => billable.has(i.key)));
            this.syncArm();
        });
    }

    isSelected(key: string): boolean {
        return this.selectedKeys().has(key);
    }

    toggleSelected(row: RechnungsversandRow): void {
        if (!row.billable) {
            return;
        }
        this.facade.belegSelection.update(prev =>
            prev.some(i => i.key === row.key)
                ? prev.filter(i => i.key !== row.key)
                : [...prev, { key: row.key, source: row.source, productId: row.productId, name: row.name }],
        );
        this.syncArm();
    }

    toggleSelectAll(): void {
        this.facade.belegSelection.set(
            this.allSelected()
                ? []
                : this.billableRows().map(r => ({ key: r.key, source: r.source, productId: r.productId, name: r.name })),
        );
        this.syncArm();
    }

    setKind(kind: BelegKind): void {
        this.facade.belegIntent.update(b => ({ ...b, kind }));
    }

    // Arm the post-save Beleg: the booking is not saved yet, so the prefilled
    // form is opened by the entry layer after the wizard closes.
    armBeleg(): void {
        if (this.selectedCount() === 0) {
            return;
        }
        this.facade.belegIntent.update(b => ({ ...b, create: true }));
    }

    disarm(): void {
        if (this.facade.belegIntent().create) {
            this.facade.belegIntent.update(b => ({ ...b, create: false }));
        }
    }

    // Keep an armed Beleg stable across selection changes, but never leave it
    // armed with nothing selected (there would be nothing to transfer).
    private syncArm(): void {
        if (this.selectedCount() === 0) {
            this.disarm();
        }
    }
}
